import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { entities, settings, media, tags as tagsApi } from "../services/api";
import {
  BackIcon,
  CloseIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  DocumentIcon,
  LinkIcon,
} from "../components/Icons";
import TagMultiSelect from "../components/TagMultiSelect";
import SearchableSelect from "../components/SearchableSelect";
import { toast } from "../components/Toast";
import { extractProductData, parseReceipt } from "../utils/receiptParser";

// Marketplace boilerplate default — only applies to products
const PRODUCT_DESCRIPTION_DEFAULT =
  "Brand new. Open to offers or trades. If this listing is up, it's available.";

function CreateEntity() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qrCode = searchParams.get("qr");
  const parentIdParam = searchParams.get("parent");
  const typeParam = searchParams.get("type");

  const [entityType, setEntityType] = useState(typeParam || "product");
  const [name, setName] = useState("");
  const [description, setDescription] = useState(
    (typeParam || "product") === "product" ? PRODUCT_DESCRIPTION_DEFAULT : "",
  );
  const [selectedParentId, setSelectedParentId] = useState(parentIdParam || "");
  const [parentEntity, setParentEntity] = useState(null);
  const [parentSearch, setParentSearch] = useState("");
  const [parentSearchResults, setParentSearchResults] = useState([]);
  const [showParentDropdown, setShowParentDropdown] = useState(false);

  // Product fields
  const [sku, setSku] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [minSalePrice, setMinSalePrice] = useState("");
  const [listSalePrice, setListSalePrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [condition, setCondition] = useState("new");
  const [productUrl, setProductUrl] = useState("");
  const [pendingImageUrl, setPendingImageUrl] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");

  // Container fields
  const [capacity, setCapacity] = useState("");
  const [containerType, setContainerType] = useState("");

  // Bulk container creation: name is the series start (e.g. "SB-15"),
  // bulkEnd is the last name or number (e.g. "SB-30" or "30")
  const [bulkCreate, setBulkCreate] = useState(false);
  const [bulkEnd, setBulkEnd] = useState("");
  const [bulkProgress, setBulkProgress] = useState(null); // "5/16" while creating

  // Location fields
  const [address, setAddress] = useState("");
  const [locationType, setLocationType] = useState("");

  // Settings-based options
  const [containerTypes, setContainerTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);

  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  const [loading, setLoading] = useState(false);

  // Set after a container is created (outside the QR flow) to offer creating
  // another one with the same settings
  const [createdContainer, setCreatedContainer] = useState(null);

  // Receipt scanning state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptText, setReceiptText] = useState("");
  const [receiptHtml, setReceiptHtml] = useState("");
  const [parsedProducts, setParsedProducts] = useState([]);
  const [receiptStep, setReceiptStep] = useState("paste"); // paste | select
  const editorRef = useRef(null);
  const nameInputRef = useRef(null);

  // Load settings for dropdown options
  useEffect(() => {
    settings.get().then(({ data }) => {
      setContainerTypes(data.containerTypes || []);
      setLocationTypes(data.locationTypes || []);
      // Set defaults
      if (data.containerTypes?.length) setContainerType(data.containerTypes[0]);
      if (data.locationTypes?.length) setLocationType(data.locationTypes[0]);
    });
    tagsApi.list().then(({ data }) => {
      setAllTags(data);
    });
  }, []);

  // Load parent entity if parentIdParam is provided
  useEffect(() => {
    if (parentIdParam) {
      entities
        .get(parentIdParam)
        .then(({ data }) => {
          setParentEntity(data);
          // Set default entity type based on parent
          if (!typeParam) {
            if (data.entity_type === "location") {
              setEntityType("container");
            } else if (data.entity_type === "container") {
              setEntityType("product");
            }
          }
        })
        .catch((err) => {
          console.error("Failed to load parent entity:", err);
        });
    }
  }, [parentIdParam, typeParam]);

  // Search for parent entities with debounce
  useEffect(() => {
    if (entityType === "location" || !parentSearch.trim()) {
      setParentSearchResults([]);
      return;
    }

    const searchTimer = setTimeout(() => {
      // Filter by type server-side: one query per allowed parent type, so
      // matching containers aren't crowded out of the limit by products
      const types =
        entityType === "product" ? ["container", "location"] : ["location"];
      Promise.all(
        types.map((t) =>
          entities.list({ search: parentSearch, entity_type: t, limit: 10 }),
        ),
      )
        .then((results) => {
          setParentSearchResults(results.flatMap((r) => r.data || []));
        })
        .catch((err) => {
          console.error("Failed to search for parents:", err);
        });
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [parentSearch, entityType]);

  // Clear parent when entity type changes to location
  useEffect(() => {
    if (entityType === "location") {
      setSelectedParentId("");
      setParentEntity(null);
      setParentSearch("");
    }
  }, [entityType]);

  // Swap the marketplace boilerplate description in/out when the type changes
  useEffect(() => {
    if (entityType === "product") {
      setDescription((prev) =>
        prev === "" ? PRODUCT_DESCRIPTION_DEFAULT : prev,
      );
    } else {
      setDescription((prev) =>
        prev === PRODUCT_DESCRIPTION_DEFAULT ? "" : prev,
      );
    }
  }, [entityType]);

  // Expand the bulk series into names, or return an error string.
  // Start comes from the name field ("SB-15"); end is a full name or bare
  // number ("SB-30" or "30"). Zero-padding from the start is preserved.
  const BULK_LIMIT = 100;
  const parseSeries = () => {
    const startMatch = name.trim().match(/^(.*?)(\d+)$/);
    if (!startMatch) return { error: "Name must end with a number" };
    const [, prefix, startDigits] = startMatch;
    const endMatch = bulkEnd.trim().match(/^(.*?)(\d+)$/);
    if (!endMatch) return { error: "Enter the last name or number" };
    if (endMatch[1] && endMatch[1] !== prefix)
      return { error: `Prefix must match "${prefix}"` };
    const start = parseInt(startDigits, 10);
    const end = parseInt(endMatch[2], 10);
    if (end < start) return { error: "End must be ≥ start" };
    if (end - start + 1 > BULK_LIMIT)
      return { error: `Limit is ${BULK_LIMIT} at a time` };
    const names = [];
    for (let i = start; i <= end; i++) {
      names.push(`${prefix}${String(i).padStart(startDigits.length, "0")}`);
    }
    return { names };
  };

  // "BOX 12" -> "BOX 13" (zero-padding preserved); no trailing number appends " 2"
  const incrementName = (n) => {
    const match = n.trim().match(/^(.*?)(\d+)$/);
    if (!match) return `${n.trim()} 2`;
    const next = String(parseInt(match[2], 10) + 1).padStart(
      match[2].length,
      "0",
    );
    return `${match[1]}${next}`;
  };

  // Helper function to round price to nearest $5 or $10 (never below the
  // increment, so small prices round up instead of zeroing out)
  const roundPrice = (price, roundTo = 5) => {
    if (!price || isNaN(price)) return "";
    const num = parseFloat(price);
    return Math.max(roundTo, Math.round(num / roundTo) * roundTo).toFixed(2);
  };

  // True when the form holds input the user would lose by leaving
  const hasUnsavedInput = () =>
    !loading &&
    (name.trim() !== "" ||
      description !==
        (entityType === "product" ? PRODUCT_DESCRIPTION_DEFAULT : "") ||
      sku !== "" ||
      purchasePrice !== "" ||
      retailPrice !== "" ||
      minSalePrice !== "" ||
      listSalePrice !== "" ||
      productUrl !== "" ||
      purchaseDate !== "" ||
      capacity !== "" ||
      bulkEnd !== "" ||
      address !== "" ||
      selectedTagIds.length > 0);

  // Warn before the browser unloads the page with unsaved input
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedInput()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  });

  const handleSelectParent = (parent) => {
    setSelectedParentId(parent.id);
    setParentEntity(parent);
    setParentSearch(parent.name);
    setShowParentDropdown(false);
  };

  const handleClearParent = () => {
    setSelectedParentId("");
    setParentEntity(null);
    setParentSearch("");
  };

  // Receipt modal handlers
  const handleOpenReceiptModal = () => {
    setShowReceiptModal(true);
    setReceiptStep("paste");
    setReceiptText("");
    setReceiptHtml("");
    setParsedProducts([]);
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
  };

  // Strip active content (scripts, event handlers, javascript: URLs) from
  // pasted HTML before it touches innerHTML — the clipboard can carry
  // markup from arbitrary third-party pages.
  const sanitizeReceiptHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc
      .querySelectorAll("script, style, iframe, object, embed, link, meta")
      .forEach((el) => el.remove());
    doc.querySelectorAll("*").forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (
          name.startsWith("on") ||
          ((name === "href" || name === "src" || name === "xlink:href") &&
            attr.value.trim().toLowerCase().startsWith("javascript:"))
        ) {
          el.removeAttribute(attr.name);
        }
      }
    });
    return doc.body.innerHTML;
  };

  const handleReceiptPaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    if (clipboardData) {
      const html = clipboardData.getData("text/html");
      const text = clipboardData.getData("text/plain");

      if (html) {
        const safeHtml = sanitizeReceiptHtml(html);
        setReceiptHtml(safeHtml);
        editorRef.current.innerHTML = safeHtml;
      } else {
        editorRef.current.innerText = text;
      }
      setReceiptText(text);
    }
  };

  const handleReceiptInput = () => {
    if (editorRef.current) {
      setReceiptText(editorRef.current.innerText);
      setReceiptHtml(editorRef.current.innerHTML);
    }
  };

  const handleParseReceipt = () => {
    const productLinks = receiptHtml ? extractProductData(receiptHtml) : {};
    const products = parseReceipt(receiptText, productLinks);
    setParsedProducts(products);
    setReceiptStep("select");
  };

  const handleSelectProduct = (product) => {
    // Fill in the form with the selected product
    setName(product.name);
    if (product.purchasePrice !== null && product.purchasePrice !== undefined) {
      setPurchasePrice(product.purchasePrice.toFixed(2));
    }
    if (product.retailPrice !== null && product.retailPrice !== undefined) {
      setRetailPrice(product.retailPrice.toFixed(2));
      setListSalePrice(product.retailPrice.toFixed(2));
    }
    if (product.quantity) {
      setQuantity(String(product.quantity));
    }
    if (product.asin) {
      setSku(product.asin);
    }
    if (product.url) {
      setProductUrl(product.url);
    }
    if (product.imageUrl) {
      setPendingImageUrl(product.imageUrl);
    }
    if (product.purchaseDate) {
      setPurchaseDate(product.purchaseDate.toISOString().split("T")[0]);
    }
    if (product.source) {
      setDescription(`Added from ${product.source}`);
    }

    setShowReceiptModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      entity_type: entityType,
      name,
      description: description || undefined,
      parent_id: selectedParentId || undefined,
    };

    // Include QR code if provided
    if (qrCode) {
      data.qr_code = qrCode;
    }

    // Add type-specific fields
    if (entityType === "product") {
      if (sku) data.sku = sku;
      if (purchasePrice) data.purchase_price = parseFloat(purchasePrice);
      if (retailPrice) data.retail_price = parseFloat(retailPrice);
      if (minSalePrice) data.min_sale_price = parseFloat(minSalePrice);
      if (listSalePrice) data.list_sale_price = parseFloat(listSalePrice);
      if (quantity) data.quantity = parseInt(quantity, 10);
      if (condition) data.condition = condition;
      if (productUrl) data.url = productUrl;
      if (purchaseDate) data.purchase_date = purchaseDate;
    } else if (entityType === "container") {
      if (capacity) data.capacity = parseInt(capacity, 10);
      if (containerType) data.container_type = containerType;
    } else if (entityType === "location") {
      if (address) data.address = address;
      if (locationType) data.location_type = locationType;
    }

    // Bulk container series: create one entity per name, sequentially so a
    // failure can resume from where it stopped
    if (entityType === "container" && bulkCreate && !qrCode) {
      const series = parseSeries();
      if (series.error) {
        toast.error(series.error);
        setLoading(false);
        return;
      }
      let created = 0;
      try {
        for (const seriesName of series.names) {
          setBulkProgress(`${created + 1}/${series.names.length}`);
          const res = await entities.create({ ...data, name: seriesName });
          if (selectedTagIds.length > 0) {
            try {
              await entities.setTags(res.data.id, selectedTagIds);
            } catch (tagErr) {
              console.error("Failed to set tags:", tagErr);
            }
          }
          created++;
        }
        toast.success(`Created ${created} containers`);
        navigate(
          selectedParentId ? `/entity/${selectedParentId}` : "/containers",
        );
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to create container";
        console.error("Bulk create failed:", err);
        toast.error(
          `Created ${created} of ${series.names.length} — ${errorMessage}`,
        );
        // Resume from the name that failed
        setName(series.names[created]);
        setLoading(false);
        setBulkProgress(null);
      }
      return;
    }

    try {
      const response = await entities.create(data);
      const createdId = response.data.id;

      // Download and save image if we have a pending image URL
      if (pendingImageUrl && entityType === "product") {
        try {
          await media.downloadFromUrl(pendingImageUrl, createdId);
        } catch (imgErr) {
          console.error("Failed to download image:", imgErr);
        }
      }

      // Save tags if any selected
      if (selectedTagIds.length > 0) {
        try {
          await entities.setTags(createdId, selectedTagIds);
        } catch (tagErr) {
          console.error("Failed to set tags:", tagErr);
        }
      }

      // Containers are usually created in batches — offer to keep going with
      // the same settings and an auto-incremented name. Skip in the QR-linking
      // flow, where each create is tied to one scanned code.
      if (entityType === "container" && !qrCode) {
        setCreatedContainer({ id: createdId, name });
        setLoading(false);
        return;
      }

      navigate(`/entity/${createdId}`);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Failed to create entity";
      console.error("Failed to create entity:", err);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleCreateAnother = () => {
    toast.success(`Created ${createdContainer.name}`, {
      action: { url: `/entity/${createdContainer.id}`, label: "View" },
    });
    setName(incrementName(createdContainer.name));
    setCreatedContainer(null);
    // Defer until after the re-render so the new name is what gets selected
    setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 0);
  };

  const handleDoneCreating = () => {
    navigate(`/entity/${createdContainer.id}`);
  };

  const getIcon = (type, size = 20) => {
    switch (type) {
      case "product":
        return <ProductIcon size={size} />;
      case "container":
        return <ContainerIcon size={size} />;
      case "location":
        return <LocationIcon size={size} />;
      default:
        return <DocumentIcon size={size} />;
    }
  };

  // Determine which entity types can be added based on parent
  const getAvailableTypes = () => {
    if (!parentEntity) {
      return ["product", "container", "location"];
    }
    if (parentEntity.entity_type === "location") {
      return ["product", "container"];
    }
    if (parentEntity.entity_type === "container") {
      return ["product"];
    }
    return [];
  };

  const availableTypes = getAvailableTypes();
  const bulkSeries =
    entityType === "container" && bulkCreate && name.trim() && bulkEnd.trim()
      ? parseSeries()
      : null;
  // Show type selector when: no type pre-selected via URL AND multiple types available
  const showTypeSelector = !typeParam && availableTypes.length > 1;

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => {
              if (
                hasUnsavedInput() &&
                !window.confirm("Discard unsaved changes?")
              ) {
                return;
              }
              navigate(-1);
            }}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">
              {typeParam
                ? `Add ${typeParam.charAt(0).toUpperCase() + typeParam.slice(1)}`
                : "Add New Item"}
            </h1>
            <p className="header-subtitle">
              {typeParam
                ? `Create a new ${typeParam}`
                : "Create a new product, container, or location"}
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Parent Entity Badge - only when parent is locked via URL */}
        {parentEntity && parentIdParam && (
          <div
            className="card"
            style={{
              marginBottom: "var(--spacing-lg)",
              padding: "var(--spacing-md)",
              background: "var(--bg-tertiary)",
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
            }}
          >
            <span style={{ fontSize: "1.5rem" }}>
              {getIcon(parentEntity.entity_type)}
            </span>
            <div>
              <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
                Adding to
              </div>
              <div style={{ fontWeight: "600" }}>{parentEntity.name}</div>
            </div>
          </div>
        )}

        {/* QR Code Badge */}
        {qrCode && (
          <div
            className="card"
            style={{
              marginBottom: "var(--spacing-lg)",
              padding: "var(--spacing-md)",
              background: "var(--bg-tertiary)",
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
            }}
          >
            <span style={{ fontSize: "1.5rem" }}>📱</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
                Linking to QR Code
              </div>
              <div style={{ fontFamily: "monospace", fontWeight: "600" }}>
                {qrCode}
              </div>
            </div>
          </div>
        )}

        {/* Scan Receipt Button - for products */}
        {entityType === "product" && (
          <button
            type="button"
            onClick={handleOpenReceiptModal}
            className="btn btn-secondary"
            style={{ width: "100%", marginBottom: "var(--spacing-lg)" }}
          >
            <DocumentIcon size={16} />
            <span style={{ marginLeft: "8px" }}>
              Scan Receipt to Fill Details
            </span>
          </button>
        )}

        <form onSubmit={handleSubmit}>
          {/* Type Selection - Only show if not pre-selected */}
          {showTypeSelector && (
            <div className="quick-actions">
              {availableTypes.map((type) => (
                <div
                  key={type}
                  onClick={() => setEntityType(type)}
                  className="quick-action"
                  style={{
                    borderColor:
                      entityType === type
                        ? "var(--accent-primary)"
                        : "var(--border-color)",
                    background:
                      entityType === type
                        ? "var(--bg-tertiary)"
                        : "var(--bg-card)",
                  }}
                >
                  <div className="quick-action-icon">{getIcon(type)}</div>
                  <div
                    className="quick-action-label"
                    style={{ textTransform: "capitalize" }}
                  >
                    {type}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Main Form */}
          <div className="card">
            <div className="form-group">
              <label>Name *</label>
              <input
                ref={nameInputRef}
                type="text"
                placeholder={`Enter ${entityType} name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                placeholder="Optional description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="3"
              />
            </div>

            {/* Parent selector - for products and containers when parent not locked */}
            {!parentIdParam && entityType !== "location" && (
              <div className="form-group" style={{ position: "relative" }}>
                <label>
                  {entityType === "product"
                    ? "Location / Container"
                    : "Location"}
                </label>
                {selectedParentId && parentEntity ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-sm)",
                      padding: "var(--spacing-sm) var(--spacing-md)",
                      background: "var(--bg-tertiary)",
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <span>{getIcon(parentEntity.entity_type)}</span>
                    <span style={{ flex: 1 }}>{parentEntity.name}</span>
                    <button
                      type="button"
                      onClick={handleClearParent}
                      className="btn btn-ghost btn-icon"
                      style={{ padding: "4px 8px" }}
                    >
                      <CloseIcon size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Search for location or container..."
                      value={parentSearch}
                      onChange={(e) => {
                        setParentSearch(e.target.value);
                        setShowParentDropdown(true);
                      }}
                      onFocus={() => setShowParentDropdown(true)}
                      onBlur={() =>
                        setTimeout(() => setShowParentDropdown(false), 200)
                      }
                    />
                    {showParentDropdown && parentSearchResults.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          background: "var(--bg-card)",
                          border: "1px solid var(--border-color)",
                          borderRadius: "var(--radius-md)",
                          boxShadow: "var(--shadow-lg)",
                          zIndex: 100,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {parentSearchResults.map((p) => (
                          <div
                            key={p.id}
                            onClick={() => handleSelectParent(p)}
                            style={{
                              padding: "var(--spacing-sm) var(--spacing-md)",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "var(--spacing-sm)",
                              borderBottom: "1px solid var(--border-color)",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.background = "var(--bg-tertiary)")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.background = "transparent")
                            }
                          >
                            <span>{getIcon(p.entity_type)}</span>
                            <span>{p.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Product-specific fields */}
            {entityType === "product" && (
              <>
                <div className="form-group">
                  <label>SKU / Barcode</label>
                  <input
                    type="text"
                    placeholder="Product identifier (optional)"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                  />
                </div>

                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      placeholder="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Condition</label>
                    <SearchableSelect
                      options={[
                        { value: "new", label: "New" },
                        { value: "like_new", label: "Like New" },
                        { value: "good", label: "Good" },
                        { value: "fair", label: "Fair" },
                        { value: "poor", label: "Poor" },
                      ]}
                      value={condition}
                      onChange={(val) => setCondition(val || "new")}
                    />
                  </div>
                </div>

                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Purchase Price</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label>Retail Price</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={retailPrice}
                      onChange={(e) => setRetailPrice(e.target.value)}
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="grid grid-2" style={{ marginBottom: 0 }}>
                  <div className="form-group">
                    <label>Minimum Sale Price</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={minSalePrice}
                      onChange={(e) => setMinSalePrice(e.target.value)}
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label>List Sale Price</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={listSalePrice}
                        onChange={(e) => setListSalePrice(e.target.value)}
                        step="0.01"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setListSalePrice(roundPrice(listSalePrice, 5))}
                        title="Round to nearest $5"
                        style={{
                          padding: "8px 12px",
                          background: "var(--bg-secondary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        → $5
                      </button>
                      <button
                        type="button"
                        onClick={() => setListSalePrice(roundPrice(listSalePrice, 10))}
                        title="Round to nearest $10"
                        style={{
                          padding: "8px 12px",
                          background: "var(--bg-secondary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        → $10
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Container-specific fields */}
            {entityType === "container" && (
              <>
                <div className="grid grid-2" style={{ marginBottom: 0 }}>
                  <div className="form-group">
                    <label>Container Type</label>
                    <SearchableSelect
                      options={containerTypes.map((type) => ({
                        value: type,
                        label: type.charAt(0).toUpperCase() + type.slice(1),
                      }))}
                      value={containerType}
                      onChange={(val) => setContainerType(val)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Capacity</label>
                    <input
                      type="number"
                      placeholder="Max items (optional)"
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                {/* Bulk series creation — not in the QR flow, where the
                    create is tied to one scanned code */}
                {!qrCode && (
                  <>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--spacing-sm)",
                        marginTop: "var(--spacing-md)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={bulkCreate}
                        onChange={(e) => setBulkCreate(e.target.checked)}
                        style={{ width: "auto", margin: 0 }}
                      />
                      Create a series
                      <span
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "0.8rem",
                        }}
                      >
                        e.g. SB-15 through SB-30
                      </span>
                    </label>
                    {bulkCreate && (
                      <div
                        className="form-group"
                        style={{
                          marginTop: "var(--spacing-md)",
                          marginBottom: 0,
                        }}
                      >
                        <label>Through *</label>
                        <input
                          type="text"
                          placeholder="Last name or number (e.g. SB-30 or 30)"
                          value={bulkEnd}
                          onChange={(e) => setBulkEnd(e.target.value)}
                          required
                        />
                        {bulkSeries && (
                          <p
                            style={{
                              marginTop: "var(--spacing-xs)",
                              fontSize: "0.8rem",
                              color: bulkSeries.error
                                ? "var(--accent-danger)"
                                : "var(--text-muted)",
                            }}
                          >
                            {bulkSeries.error ||
                              `${bulkSeries.names[0]} → ${
                                bulkSeries.names[bulkSeries.names.length - 1]
                              } (${bulkSeries.names.length} containers)`}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Location-specific fields */}
            {entityType === "location" && (
              <>
                <div className="form-group">
                  <label>Location Type</label>
                  <SearchableSelect
                    options={locationTypes.map((type) => ({
                      value: type,
                      label: type.charAt(0).toUpperCase() + type.slice(1),
                    }))}
                    value={locationType}
                    onChange={(val) => setLocationType(val)}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Address</label>
                  <textarea
                    placeholder="Physical address (optional)"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    rows="2"
                  />
                </div>
              </>
            )}

            {/* Tags */}
            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label>Tags</label>
              <TagMultiSelect
                allTags={allTags}
                selectedTagIds={selectedTagIds}
                onTagsChange={setSelectedTagIds}
                onCreateTag={async (name) => {
                  const { data } = await tagsApi.create({ name });
                  setAllTags([...allTags, data]);
                  return data;
                }}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
              style={{ width: "100%", marginTop: "var(--spacing-lg)" }}
            >
              {loading
                ? bulkProgress
                  ? `Creating ${bulkProgress}...`
                  : "Creating..."
                : bulkSeries && !bulkSeries.error
                  ? `Create ${bulkSeries.names.length} containers`
                  : `Create ${entityType}`}
            </button>
          </div>
        </form>
      </div>

      {/* Container Created - Create Another? Modal */}
      {createdContainer && (
        <div className="quick-add-overlay" onClick={handleDoneCreating}>
          <div
            className="quick-add-modal"
            style={{ maxWidth: "400px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quick-add-body" style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  margin: "0 auto var(--spacing-md)",
                  borderRadius: "50%",
                  background: "var(--bg-tertiary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--accent-primary)",
                }}
              >
                <ContainerIcon size={28} />
              </div>
              <h2 style={{ marginBottom: "var(--spacing-xs)" }}>
                Container created
              </h2>
              <p
                className="quick-add-description"
                style={{ marginBottom: "var(--spacing-lg)" }}
              >
                <strong>{createdContainer.name}</strong> is ready. Create
                another with the same settings?
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "var(--spacing-sm)",
                  justifyContent: "center",
                }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={handleDoneCreating}
                >
                  Done
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateAnother}
                  autoFocus
                >
                  Create Another
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Scanning Modal */}
      {showReceiptModal && (
        <div className="quick-add-overlay" onClick={handleCloseReceiptModal}>
          <div className="quick-add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quick-add-header">
              <div className="quick-add-header-content">
                <DocumentIcon size={20} />
                <h2>Scan Receipt</h2>
              </div>
              <button
                className="btn btn-ghost btn-icon"
                onClick={handleCloseReceiptModal}
              >
                <CloseIcon size={20} />
              </button>
            </div>

            {receiptStep === "paste" && (
              <div className="quick-add-body">
                <p className="quick-add-description">
                  Paste your receipt to auto-fill product details. Works with
                  Amazon orders.
                </p>
                <div
                  ref={editorRef}
                  className="quick-add-textarea quick-add-editor"
                  contentEditable
                  onPaste={handleReceiptPaste}
                  onInput={handleReceiptInput}
                  data-placeholder="Paste receipt text here..."
                />
                <div className="quick-add-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={handleCloseReceiptModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleParseReceipt}
                    disabled={!receiptText.trim()}
                  >
                    Parse Receipt
                  </button>
                </div>
              </div>
            )}

            {receiptStep === "select" && (
              <div className="quick-add-body">
                {parsedProducts.length === 0 ? (
                  <div className="quick-add-empty">
                    <ProductIcon size={48} />
                    <h3>No products found</h3>
                    <p>We couldn't detect any products in the pasted text.</p>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setReceiptStep("paste")}
                    >
                      Try Again
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="quick-add-description">
                      Found {parsedProducts.length} product
                      {parsedProducts.length !== 1 ? "s" : ""}. Select one to
                      fill the form:
                    </p>
                    <div className="quick-add-product-list">
                      {parsedProducts.map((product, index) => (
                        <button
                          key={index}
                          className="quick-add-product-item"
                          onClick={() => handleSelectProduct(product)}
                        >
                          {product.imageUrl && (
                            <img
                              src={product.imageUrl}
                              alt=""
                              className="quick-add-product-image"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <div className="quick-add-product-info">
                            <div className="quick-add-product-name">
                              {product.name}
                              {product.url && (
                                <span
                                  className="quick-add-link-badge"
                                  title="Product link captured"
                                >
                                  <LinkIcon size={12} />
                                </span>
                              )}
                            </div>
                            <div className="quick-add-product-meta">
                              {product.purchasePrice !== null &&
                                product.purchasePrice !== undefined && (
                                  <span className="quick-add-product-price">
                                    ${product.purchasePrice.toFixed(2)}
                                    {product.retailPrice !== null &&
                                      product.retailPrice !==
                                        product.purchasePrice && (
                                        <span className="quick-add-original-price">
                                          {" "}
                                          (was ${product.retailPrice.toFixed(2)}
                                          )
                                        </span>
                                      )}
                                  </span>
                                )}
                              {product.quantity > 1 && (
                                <span className="quick-add-product-qty">
                                  Qty: {product.quantity}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="quick-add-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setReceiptStep("paste")}
                      >
                        Back
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateEntity;
